import React, { useCallback } from 'react'
import { Trans } from 'react-i18next'

import { BuilderPage } from '@makyoapp/makyo-common'
import { AlignCenter16, AlignLeft16, AlignRight16 } from '@makyoapp/makyo-icon/icon'
import { Field, SingleIconSelect } from '@makyoapp/makyo-ui'

import { useAppPagesContext } from '@/context/AppPagesContext'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'
import { BrandKitColorsDropdown } from '@/components/BrandKitColorsDropdown'

interface PageSettingDesignProps {
  page: BuilderPage
  pageIdx: number
  search: string
}

export const PageSettingDesign = ({ page, pageIdx, search }: PageSettingDesignProps) => {
  const { pages, updatePageAlt } = useAppPagesContext()

  const getFilteredAccordionItems = useCallback(() => {
    if (!search.trim()) {
      return {
        showBgColor: true,
        showAlignment: true,
      }
    }

    const searchLower = search.toLowerCase()

    return {
      showBgColor: 'background color'.includes(searchLower),
      showAlignment: 'alignment'.includes(searchLower) || 'content alignment'.includes(searchLower),
    }
  }, [search])

  const filteredItems = getFilteredAccordionItems()

  return (
    <Accordion openMultiple={false} initialActiveItems={['bgColor']}>
      {filteredItems.showBgColor && (
        <AccordionItem itemId='bgColor' title={<Trans>Background</Trans>}>
          <Field isVertical>
            <label>Background Color</label>

            <BrandKitColorsDropdown
              type='bg'
              value={page.bgColor}
              onChange={async (value) => {
                await updatePageAlt(pageIdx, (prev: { bgColor: string }) => {
                  prev.bgColor = value
                })
              }}
            />
          </Field>
        </AccordionItem>
      )}

      {filteredItems.showAlignment && (
        <AccordionItem itemId='alignment' title={<Trans>Alignment</Trans>}>
          <Field>
            <label>Content Alignment</label>

            <SingleIconSelect
              id='page-alignment'
              onClick={async (opt) => {
                await updatePageAlt(pageIdx, (prev) => {
                  prev.alignment = opt.id as any
                })
              }}
              options={[
                {
                  icon: <AlignLeft16 />,
                  id: 'start',
                  title: 'Start',
                },
                {
                  icon: <AlignCenter16 />,
                  id: 'center',
                  title: 'Center',
                },
                {
                  icon: <AlignRight16 />,
                  id: 'end',
                  title: 'End',
                },
              ]}
              selected={
                ['start', 'center', 'end'].includes(page.alignment as string) ? (page.alignment as string) : 'center'
              }
            />
          </Field>
        </AccordionItem>
      )}
    </Accordion>
  )
}
